<template>
  <div class="min-h-screen bg-surface-raised dark:bg-surface-raised-dark flex flex-col items-center pt-[17vh] px-4">
    <div class="max-w-md w-full space-y-2">
      <!-- Header -->
      <div class="text-center">
        <div class="mx-auto h-[134px] w-[134px] rounded-lg flex items-center justify-center">
          <img v-if="logoUrl" :src="logoUrl" class="max-h-[134px] max-w-[134px] w-auto h-auto object-contain" alt="" />
          <!-- hand-drawn placeholder, kept for now in case we want it back:
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"
               stroke-linecap="round" stroke-linejoin="round"
               class="h-9 w-9 text-fg-secondary dark:text-fg-secondary-dark">
            <path d="M14.5 3.5 4 14a3.5 3.5 0 1 0 5 5L19.5 8.5" />
            <circle cx="17" cy="6" r="2" fill="currentColor" stroke="none" />
            <path d="M14 8.5 9.5 4" />
            <path d="M12.5 12 9 9" />
          </svg>
          -->
          <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="h-[101px] w-[101px] flex-shrink-0">
            <path fill="#1b62da" d="M19 9c0-1.65-1.35-3-3-3s-3 1.35-3 3v9c0 .55-.45 1-1 1s-1-.45-1-1V6c0-1.65-1.35-3-3-3S5 4.35 5 6v5H2v2h4c.55 0 1-.45 1-1V6c0-.55.45-1 1-1s1 .45 1 1v12c0 1.65 1.35 3 3 3s3-1.35 3-3V9c0-.55.45-1 1-1s1 .45 1 1v3c0 .55.45 1 1 1h4v-2h-3z"></path>
          </svg>
        </div>
        <h2 class="mt-2 text-3xl font-bold text-fg-base dark:text-fg-base-dark leading-tight">
          {{ name || 'Teacher' }}<br />Dashboard Login
        </h2>
      </div>

      <!-- Invite processing state -->
      <div v-if="inviteProcessing" class="mt-6 text-center text-fg-secondary dark:text-fg-secondary-dark text-sm">
        Verifying your invite link…
      </div>

      <!-- Login Form -->
      <form v-if="!inviteProcessing" @submit.prevent="handleLogin" class="mt-2 space-y-2">
        <div class="space-y-2">
          <!-- Email -->
          <div>
            <label for="email" class="block text-sm font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">
              Email
            </label>
            <input
              id="email"
              ref="emailRef"
              v-model="form.email"
              type="email"
              required
              class="input w-full"
              :class="{ 'border-red-500': errors.email }"
              placeholder="Enter your email"
              autocomplete="username"
            />
            <p v-if="errors.email" class="mt-1 text-sm text-red-600 dark:text-red-400">
              {{ errors.email }}
            </p>
          </div>

          <!-- Password -->
          <div>
            <label for="password" class="block text-sm font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">
              Password
            </label>
            <input
              id="password"
              ref="passwordRef"
              v-model="form.password"
              type="password"
              required
              class="input w-full"
              :class="{ 'border-red-500': errors.password }"
              placeholder="Enter your password"
              autocomplete="current-password"
            />
            <p v-if="errors.password" class="mt-1 text-sm text-red-600 dark:text-red-400">
              {{ errors.password }}
            </p>
          </div>

          <!-- Remember Me -->
          <div class="flex items-center">
            <input
              id="remember-me"
              v-model="form.rememberMe"
              type="checkbox"
              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-stroke-strong rounded"
            />
            <label for="remember-me" class="ml-2 block text-sm text-fg-secondary dark:text-fg-secondary-dark">
              Remember me on this device
            </label>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="rounded-md bg-red-50 dark:bg-red-900/30 p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-red-700 dark:text-red-300">{{ errorMessage }}</p>
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <div>
          <button
            type="submit"
            :disabled="isLoading"
            class="btn-primary w-full flex justify-center py-2 px-4"
            :class="{ 'opacity-50 cursor-not-allowed': isLoading }"
          >
            <svg v-if="isLoading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>{{ isLoading ? 'Signing in...' : 'Sign in' }}</span>
          </button>
        </div>
      </form>

    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useTenantBranding } from '@/composables/useTenantBranding'

const router = useRouter()
const route = useRoute()
const { name, logoUrl } = useTenantBranding()

// Form state
const form = reactive({
  email: '',
  password: '',
  rememberMe: false
})

// Input DOM refs for autofill detection
const emailRef = ref(null)
const passwordRef = ref(null)

// UI state
const isLoading = ref(false)
const errorMessage = ref('')
const errors = reactive({
  email: '',
  password: ''
})

// Invite flow
const inviteProcessing = ref(false)

// Check if we're in development mode
const isDevelopment = computed(() => {
  return import.meta.env.MODE === 'development' || import.meta.env.DEV
})

// Handle login submission
const handleLogin = async () => {
  errorMessage.value = ''
  errors.email = ''
  errors.password = ''

  if (!form.email.trim()) {
    errors.email = 'Email is required'
    return
  }

  if (!form.password) {
    errors.password = 'Password is required'
    return
  }

  isLoading.value = true

  try {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: form.email,
        password: form.password,
        remember_me: form.rememberMe
      })
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.detail || 'Login failed')
    }

    localStorage.setItem('user', JSON.stringify(data.user))
    // Only same-origin paths — never an absolute or protocol-relative URL.
    const redirect = route.query.redirect
    router.push(typeof redirect === 'string' && redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : '/')

  } catch (error) {
    console.error('Login error:', error)
    errorMessage.value = error.message || 'Invalid email or password'
  } finally {
    isLoading.value = false
  }
}

const handleInviteToken = async (token) => {
  inviteProcessing.value = true
  try {
    const res = await fetch('/api/auth/accept-invite', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
    const data = await res.json()
    if (!res.ok) {
      errorMessage.value = data.detail || 'Invalid or expired invite link.'
      inviteProcessing.value = false
      return
    }
    localStorage.setItem('user', JSON.stringify(data.user))
    // The dashboard itself shows the "set your password" overlay (see App.vue) —
    // land there directly instead of a bare form so it's dimmed behind the modal,
    // not a blank login page the user will never otherwise see.
    router.push('/')
  } catch {
    errorMessage.value = 'Something went wrong. Please try again.'
    inviteProcessing.value = false
  }
}

onMounted(() => {
  const inviteToken = route.query.invite_token
  if (inviteToken) {
    handleInviteToken(inviteToken)
    return
  }

  // Auto-submit when the browser autofills saved credentials.
  // Browsers populate inputs directly in the DOM without firing reactive events,
  // so we read the DOM refs after a short delay rather than relying on v-model.
  setTimeout(() => {
    const email = emailRef.value?.value
    const password = passwordRef.value?.value
    if (email && password) {
      form.email = email
      form.password = password
      handleLogin()
    }
  }, 600)
})
</script>

<style scoped>
.input {
  @apply appearance-none relative block w-full px-3 py-2 border border-stroke-strong dark:border-stroke-strong-dark
         placeholder-fg-muted dark:placeholder-fg-muted-dark text-fg-base dark:text-fg-base-dark
         rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
         dark:bg-surface-dark dark:focus:ring-blue-400 dark:focus:border-blue-400;
}

.btn-primary {
  @apply bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600
         text-white font-medium rounded-lg focus:outline-none focus:ring-2
         focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900
         transition-colors duration-200;
}
</style>
